import { HelpPopover } from "../../components/HelpPopover.tsx";

type TendencyOverrideControlProps = {
	label: string;
	description: string;
	fieldKey: string;
	value: number | undefined;
	locked?: boolean | undefined;
	disabled?: boolean;
	onChange: (field: string, value: number | undefined) => void;
	onLockChange?: (field: string, locked: boolean) => void;
};

const TendencyOverrideControl = ({
	label,
	description,
	fieldKey,
	value,
	locked,
	disabled = false,
	onChange,
	onLockChange,
}: TendencyOverrideControlProps) => {
	const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = event.target.value;

		// Allow empty input to clear the override
		if (inputValue === "") {
			onChange(fieldKey, undefined);
			return;
		}

		const numValue = parseFloat(inputValue);

		if (isNaN(numValue)) {
			return;
		}

		// Clamp value to minimum of -100, but allow unlimited positive values
		const clampedValue = Math.max(-100, numValue);
		onChange(fieldKey, clampedValue);
	};

	const handleLockChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onLockChange?.(fieldKey, event.target.checked);
	};

	return (
		<div className="mb-3">
			<label className="form-label d-flex align-items-center">
				{label}
				<HelpPopover title={label}>
					{description}
					<br />
					<br />
					<strong>Range:</strong> -100 to unlimited positive
					<br />
					<strong>Negative values:</strong> Decrease tendency
					<br />
					<strong>Positive values:</strong> Increase tendency
					<br />
					<strong>Zero:</strong> Use calculated tendency
					<br />
					<br />
					<strong>Direct Multiplier Examples:</strong>
					<br />
					-100 = 0.0x (complete disable)
					<br />
					0 = calculated behavior (no multiplier)
					<br />
					0.5 = 0.5x (half league effect)
					<br />
					1.0 = 1.0x (exactly 1x league effect)
					<br />
					1.5 = 1.5x (one and a half league effect)
					<br />
					2.0 = 2.0x (exactly 2x league effect)
					<br />
					10.0 = 10.0x (exactly 10x league effect)
					<br />
					100.0 = 100.0x (exactly 100x league effect)
					<br />
					1000.0 = 1000.0x (exactly 1000x league effect)
					<br />
					<br />
					<strong className="text-danger">⚠️ Warning:</strong> Values over 100 can cause extreme and unrealistic game effects.
				</HelpPopover>
			</label>

			<div className="input-group">
				<input
					type="number"
					className="form-control"
					style={{ maxWidth: "100px", textAlign: "center", fontSize: "14px" }}
					min="-100"
					step="0.1"
					value={value ?? ""}
					placeholder="0"
					disabled={disabled}
					onChange={handleNumberChange}
				/>

				{onLockChange ? (
					<div className="form-check ms-2 d-flex align-items-center">
						<input
							className="form-check-input"
							type="checkbox"
							checked={locked ?? false}
							disabled={disabled}
							onChange={handleLockChange}
							id={`lock-${fieldKey}`}
						/>
						<label
							className="form-check-label ms-1"
							htmlFor={`lock-${fieldKey}`}
						>
							Lock
						</label>
					</div>
				) : null}
			</div>

			<div className="form-text">
				{value === undefined || value === 0 ? (
					<span className="text-muted">Using calculated tendency</span>
				) : (
					<>
						<span className={value > 0 ? "text-success" : "text-warning"}>
							Override: {value > 0 ? "+" : ""}{value}
							{locked && " (locked)"}
						</span>
						{value > 100 && (
							<span
								className="text-danger ms-2"
								title="High values can cause extreme game effects"
							>
								⚠️ High multiplier: {value.toFixed(1)}x
							</span>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default TendencyOverrideControl;
